import Badge from "@/components/ui/badge/Badge";
import { useState, useEffect, useCallback, useRef } from "react";
import { todayISO } from "@/lib/utils";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/modal";
import ConfirmModal from "@/components/ui/modal/ConfirmModal";
import { extractPaidBillWarning } from "@/lib/paidBillWarning";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { birdSaleService } from "@/services/birdSaleService";
import { useSalePayments, PAYMENTS_EXCEED_MSG } from "@/hooks/useSalePayments";
import SalePaymentSection from "@/components/sales/SalePaymentSection";
import BuyerCreditNotice from "@/components/sales/BuyerCreditNotice";
import { flockService, buyerService } from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import DateRangeFilter, { getDefaultDateRange } from "@/components/common/DateRangeFilter";
import PrintLink from "@/components/print/PrintLink";
import { openPrintWindow, writeAndPrint } from "@/utils/printInvoice";
import { amountToWords } from "@/utils/numberToWords";
import { stateFromGSTIN } from "@/utils/gstinStates";
import type { BirdSale, Flock, Buyer } from "@/types/models";
import { useIsViewer } from "@/hooks/useRole";
import { useSortable } from "@/hooks/useSortable";
import SortableHeader from "@/components/ui/table/SortableHeader";
import { useAuthStore } from "@/store/useAuthStore";
import Pagination from "@/components/ui/pagination/Pagination";
import { deleteSaleMessage } from "./deleteSaleMessage";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

export default function BirdSalesPage() {
  const isViewer = useIsViewer();
  const { sortConfig, requestSort } = useSortable("date", "desc");
  const [entries, setEntries] = useState<BirdSale[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flocks, setFlocks] = useState<Flock[]>([]);
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [filterBuyers, setFilterBuyers] = useState<Buyer[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  // Editing a history sale (before the books start): no invoice behind it, so
  // the price may stay 0 until the sale records turn up.
  const [editingHistory, setEditingHistory] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  // Set when the server asked "money has been paid on this sale"; the next
  // press of Delete is the owner saying yes.
  const [paidWarning, setPaidWarning] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [filterFrom, setFilterFrom] = useState(() => getDefaultDateRange().from);
  const [filterTo, setFilterTo] = useState(() => getDefaultDateRange().to);
  const [filterFlockId, setFilterFlockId] = useState("");
  const [filterBuyer, setFilterBuyer] = useState("");

  // Form state
  const [date, setDate] = useState(todayISO());
  const [flockId, setFlockId] = useState("");
  const [birdCount, setBirdCount] = useState("");
  const [totalWeightKg, setTotalWeightKg] = useState("");
  const [ratePerKg, setRatePerKg] = useState("");
  const [ratePerBird, setRatePerBird] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [buyer, setBuyer] = useState("");
  const [notes, setNotes] = useState("");
  // Split-tender payments (create only, real buyer selected).
  const invoiceTotal = Math.round(parseFloat(totalAmount) || 0);
  const pay = useSalePayments(invoiceTotal);
  // Owner's explicit approval for a sale that breaches the buyer's credit terms.
  const [creditOverride, setCreditOverride] = useState(false);
  const showPayments = !editingId && buyer !== "";
  const paymentsExceedTotal = showPayments && pay.exceedsTotal;

  useEffect(() => {
    flockService.list().then(setFlocks).catch(() => {});
    buyerService.list().then((data) => {
      // The sale form offers active buyers; the FILTER offers every buyer of
      // this kind, inactive ones too — their past sales are still in the list.
      const ofKind = (data as Buyer[]).filter((b) => b.categories?.includes("birds"));
      setBuyers(ofKind.filter((b) => b.status === "active"));
      setFilterBuyers(ofKind);
    }).catch(() => {});
  }, []);

  const loadSales = useCallback(async (fromDate?: string, toDate?: string, newOffset?: number) => {
    setError("");
    try {
      const o = newOffset !== undefined ? newOffset : offset;
      const params: Record<string, string | number> = { limit, offset: o };
      const f = fromDate !== undefined ? fromDate : filterFrom;
      const t = toDate !== undefined ? toDate : filterTo;
      if (f) params.from = f;
      if (t) params.to = t;
      if (filterFlockId) params.flock_id = filterFlockId;
      if (filterBuyer) params.buyer_id = filterBuyer;
      params.sort_by = sortConfig.key;
      params.sort_order = sortConfig.direction;
      const res = await birdSaleService.list(params);
      if (Array.isArray(res)) {
        setEntries(res as unknown as BirdSale[]);
        setTotal((res as unknown as BirdSale[]).length);
      } else {
        const paginated = res as unknown as {
          data: BirdSale[];
          meta: { total: number };
        };
        setEntries(paginated.data ?? []);
        setTotal(paginated.meta?.total ?? 0);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [offset, filterFrom, filterTo, filterFlockId, filterBuyer, sortConfig]);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  // Skips the auto-calc for the one render after openEdit hydrates the form, so
  // editing an unrelated field doesn't overwrite a stored total that was
  // manually adjusted away from weight×rate / count×rate.
  // Edge: two back-to-back edits of records with identical inputs leave the flag
  // set for one extra render; it self-clears on the next change, never corrupting
  // a stored total.
  const skipAutoCalcRef = useRef(false);

  // Auto-calculate total: if rate_per_kg and total_weight set, use weight-based; else use per-bird
  useEffect(() => {
    if (skipAutoCalcRef.current) {
      skipAutoCalcRef.current = false;
      return;
    }
    if (totalWeightKg && ratePerKg) {
      const weight = parseFloat(totalWeightKg);
      const rate = parseFloat(ratePerKg);
      if (weight > 0 && rate > 0) {
        setTotalAmount(String(Math.round(weight * rate)));
        return;
      }
    }
    if (birdCount && ratePerBird) {
      const count = parseInt(birdCount);
      const rate = parseFloat(ratePerBird);
      if (count > 0 && rate > 0) {
        setTotalAmount(String(Math.round(count * rate)));
      }
    }
  }, [birdCount, totalWeightKg, ratePerKg, ratePerBird]);

  const openCreate = () => {
    setEditingId(null);
    setEditingHistory(false);
    setDate(todayISO());
    setFlockId("");
    setBirdCount("");
    setTotalWeightKg("");
    setRatePerKg("");
    setRatePerBird("");
    setTotalAmount("");
    setBuyer("");
    setNotes("");
    pay.reset();
    setError("");
    setModalOpen(true);
  };

  const openEdit = (sale: BirdSale) => {
    skipAutoCalcRef.current = true;
    setEditingId(sale.id);
    setEditingHistory(!!sale.history);
    setDate(sale.date.split("T")[0]);
    setFlockId(sale.flock_id ? String(sale.flock_id) : "");
    setBirdCount(String(sale.bird_count));
    setTotalWeightKg(sale.total_weight_kg ? String(sale.total_weight_kg) : "");
    setRatePerKg(sale.rate_per_kg ? String(sale.rate_per_kg) : "");
    setRatePerBird(sale.rate_per_bird ? String(sale.rate_per_bird) : "");
    setTotalAmount(String(Math.round(sale.total_amount)));
    setBuyer(sale.buyer_id ? String(sale.buyer_id) : "");
    setNotes(sale.notes || "");
    pay.reset(); // payments are create-only
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const data = {
        flock_id: flockId ? parseInt(flockId) : undefined,
        date,
        bird_count: parseInt(birdCount),
        total_weight_kg: totalWeightKg ? parseFloat(totalWeightKg) : undefined,
        rate_per_kg: ratePerKg ? parseFloat(ratePerKg) : undefined,
        rate_per_bird: ratePerBird ? parseFloat(ratePerBird) : undefined,
        total_amount: Math.round(parseFloat(totalAmount)),
        buyer_id: buyer ? parseInt(buyer) : undefined,
        credit_override: creditOverride,
        notes: notes || undefined,
      };
      if (editingId) {
        await birdSaleService.update(editingId, data);
      } else {
        const lines = showPayments ? pay.buildLines() : [];
        if (paymentsExceedTotal) {
          setError(PAYMENTS_EXCEED_MSG);
          setSaving(false);
          return;
        }
        await birdSaleService.create({ ...data, payments: lines.length ? lines : undefined });
      }
      setModalOpen(false);
      setLoading(true);
      await loadSales();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const closeDelete = () => {
    setDeleteConfirmId(null);
    setPaidWarning(null);
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    setDeleting(true);
    try {
      await birdSaleService.delete(deleteConfirmId, paidWarning !== null);
      closeDelete();
      setLoading(true);
      await loadSales();
    } catch (err) {
      const warning = extractPaidBillWarning(err);
      if (warning) {
        setPaidWarning(warning);
        return;
      }
      setError(handleApiError(err));
      closeDelete();
    } finally {
      setDeleting(false);
    }
  };

  const handleDateRangeApply = (from: string, to: string) => {
    setFilterFrom(from);
    setFilterTo(to);
    setOffset(0);
    setLoading(true);
    loadSales(from, to, 0);
  };

  const handlePrev = () => {
    const newOffset = Math.max(0, offset - limit);
    setOffset(newOffset);
    loadSales(undefined, undefined, newOffset);
  };

  const handleNext = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    loadSales(undefined, undefined, newOffset);
  };

  const farm = useAuthStore((s) => s.farm);
  const [printingId, setPrintingId] = useState<number | null>(null);

  const formatCurrency = (amount: number) =>
    amount.toLocaleString("en-IN", { maximumFractionDigits: 2 });

  const handlePrint = async (sale: BirdSale) => {
    const pw = openPrintWindow();
    if (!pw) return;
    setPrintingId(sale.id);
    try {
      let buyer: Buyer | null = null;
      if (sale.buyer_id) {
        try { buyer = await buyerService.get(sale.buyer_id); } catch { /* proceed without */ }
      }
      const buyerGstin = (buyer?.details as Record<string, string> | undefined)?.gstNumber;
      const buyerState = stateFromGSTIN(buyerGstin);
      const inv = farm?.invoice_settings;
      const rateStr = sale.rate_per_kg
        ? sale.rate_per_kg.toFixed(2) + "/kg"
        : sale.rate_per_bird
        ? sale.rate_per_bird.toFixed(2) + "/bird"
        : "-";
      const qtyStr = sale.bird_count.toLocaleString("en-IN")
        + (sale.total_weight_kg ? ` (${sale.total_weight_kg.toFixed(2)} kg)` : "");
      writeAndPrint(pw, {
        farmName: farm?.name || "Farm",
        productionAddress: farm?.production_address,
        registeredAddress: farm?.address,
        stateName: farm?.state_name,
        stateCode: farm?.state_code,
        msmeNumber: farm?.msme_number,
        panNumber: farm?.pan_number,
        contactPhone: farm?.phone,
        email: farm?.email,
        farmGst: farm?.gst_number,
        invoiceNumber: `BIRD-${String(sale.id).padStart(5, "0")}`,
        invoiceDate: new Date(sale.date).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }),
        buyerName: buyer?.name || sale.buyer_name || sale.buyer,
        buyerAddress: buyer?.address ?? undefined,
        buyerGstin,
        buyerStateName: buyerState?.name,
        buyerStateCode: buyerState?.code,
        lineItems: [{
          sno: 1,
          description: sale.flock_name ? `Birds (${sale.flock_name})` : "Birds",
          qty: qtyStr,
          rate: rateStr,
          amount: formatCurrency(sale.total_amount),
        }],
        totalAmount: formatCurrency(sale.total_amount),
        amountInWords: amountToWords(sale.total_amount),
        bankName: inv?.bank_name,
        bankAccountName: inv?.bank_account_name,
        bankAccountNumber: inv?.bank_account_number,
        bankBranchIfsc: inv?.bank_branch_ifsc,
        declarationText: inv?.declaration_text,
        jurisdictionText: inv?.jurisdiction_text,
        signatureUrl: inv?.signature_url,
      });
    } finally {
      setPrintingId(null);
    }
  };

  return (
    <>
      <PageMeta title="Bird Sales | Poultry Book" description="Bird sales tracking" />
      <PageBreadcrumb pageTitle="Bird Sales" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Bird Sales{" "}
            {total > 0 && (
              <span className="text-sm font-normal text-gray-500">({total})</span>
            )}
          </h3>
          {!isViewer && (
            <Button size="sm" onClick={openCreate}>
              Record Sale
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 px-5 pb-4 sm:px-6">
          <DateRangeFilter onApply={handleDateRangeApply} />
          <select
            value={filterFlockId}
            onChange={(e) => {
              setFilterFlockId(e.target.value);
              setOffset(0);
            }}
            className={`${selectClass} max-w-[200px]`}
          >
            <option value="">All Flocks</option>
            {flocks.map((f) => (
              <option key={f.id} value={f.id}>
                {f.batch_name}
              </option>
            ))}
          </select>
          <select
            value={filterBuyer}
            onChange={(e) => {
              setFilterBuyer(e.target.value);
              setOffset(0);
            }}
            className={`${selectClass} max-w-[200px]`}
          >
            <option value="">All Buyers</option>
            {filterBuyers.map((b) => (
              <option key={b.id} value={b.id}>{b.status === "active" ? b.name : `${b.name} (inactive)`}</option>
            ))}
          </select>
        </div>

        {error && !modalOpen && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">Loading...</div>
          ) : entries.length === 0 && !error ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              No bird sales in the selected period.
            </div>
          ) : entries.length === 0 ? null : (
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <SortableHeader label="Date" sortKey="date" sortConfig={sortConfig} onSort={requestSort} />
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Flock</TableCell>
                  <SortableHeader label="Birds" sortKey="bird_count" sortConfig={sortConfig} onSort={requestSort} />
                  <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">Total Wt (kg)</TableCell>
                  <SortableHeader label="Rate/kg" sortKey="rate" sortConfig={sortConfig} onSort={requestSort} />
                  <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">Rate/Bird</TableCell>
                  <SortableHeader label="Total" sortKey="amount" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Buyer" sortKey="buyer" sortConfig={sortConfig} onSort={requestSort} />
                  <TableCell isHeader className="px-5 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">{""}</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((sale) => (
                  <TableRow key={sale.id} className="border-t border-gray-100 dark:border-gray-800">
                    <TableCell className="px-5 py-3 text-sm text-gray-800 dark:text-white/90">
                      {new Date(sale.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {sale.flock_name || "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right font-medium text-gray-800 dark:text-white/90">
                      {sale.bird_count.toLocaleString()}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right text-gray-600 dark:text-gray-400">
                      {sale.total_weight_kg?.toFixed(2) ?? "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right text-gray-600 dark:text-gray-400">
                      {sale.rate_per_kg?.toFixed(2) ?? "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right text-gray-600 dark:text-gray-400">
                      {sale.rate_per_bird?.toFixed(2) ?? "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right font-medium text-gray-800 dark:text-white/90">
                      {sale.history && sale.total_amount === 0 ? (
                        <span className="text-gray-400">Not priced</span>
                      ) : (
                        formatCurrency(sale.total_amount)
                      )}
                      {sale.history && (
                        <span className="ml-2 align-middle" title="Before your books start: no invoice, not revenue">
                          <Badge color="light" size="sm">History</Badge>
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {sale.buyer_name || sale.buyer || "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <PrintLink onClick={() => handlePrint(sale)} loading={printingId === sale.id} />
                        {!isViewer && (
                          <>
                            <button onClick={() => openEdit(sale)} className="text-brand-500 hover:text-brand-600 dark:text-brand-400 text-xs font-medium">Edit</button>
                            <button onClick={() => setDeleteConfirmId(sale.id)} className="text-error-500 hover:text-error-600 dark:text-error-400 text-xs font-medium">Delete</button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <Pagination total={total} limit={limit} offset={offset} onPrev={handlePrev} onNext={handleNext} />
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-md p-5 sm:p-8 mx-4">
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editingId ? "Edit Bird Sale" : "Record Bird Sale"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="bsDate">Date *</Label>
            <Input id="bsDate" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="bsFlock">Flock</Label>
            <select id="bsFlock" value={flockId} onChange={(e) => setFlockId(e.target.value)} className={selectClass}>
              <option value="">Select flock (optional)</option>
              {flocks.map((f) => (
                <option key={f.id} value={String(f.id)}>{f.batch_name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="bsBirdCount">Bird Count *</Label>
            <Input id="bsBirdCount" type="number" value={birdCount} onChange={(e) => setBirdCount(e.target.value)} placeholder="e.g. 500" required min={1} step="1" />
          </div>
          <div>
            <Label htmlFor="bsTotalWeight">Total Weight (kg)</Label>
            <Input id="bsTotalWeight" type="number" value={totalWeightKg} onChange={(e) => setTotalWeightKg(e.target.value)} placeholder="e.g. 900" min={0} step="0.01" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="bsRateKg">Rate/kg</Label>
              <Input id="bsRateKg" type="number" value={ratePerKg} onChange={(e) => setRatePerKg(e.target.value)} placeholder="e.g. 120" min={0} step="0.01" />
            </div>
            <div>
              <Label htmlFor="bsRateBird">Rate/Bird</Label>
              <Input id="bsRateBird" type="number" value={ratePerBird} onChange={(e) => setRatePerBird(e.target.value)} placeholder="e.g. 200" min={0} step="0.01" />
            </div>
          </div>
          <div>
            <Label htmlFor="bsTotal">Total Amount * <span className="text-xs text-gray-400">auto-calculated, editable</span></Label>
            <Input id="bsTotal" type="number" value={totalAmount} onChange={(e) => setTotalAmount(e.target.value)} placeholder="e.g. 100000" required min={editingHistory ? 0 : 1} step="1" />
            {editingHistory && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                History — before your books start. No invoice is raised and it is not revenue; 0 means the price isn't known yet.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="bsBuyer">Buyer</Label>
            <select id="bsBuyer" value={buyer} onChange={(e) => setBuyer(e.target.value)} className={selectClass}>
              <option value="">Select buyer (optional)</option>
              {buyers.map((b) => (
                <option key={b.id} value={String(b.id)}>{b.name}</option>
              ))}
            </select>
          </div>
          {showPayments && (
            <SalePaymentSection pay={pay} invoiceTotal={invoiceTotal} formatCurrency={formatCurrency} />
          )}

          {/* Creation only. On an edit this sale's own receivable is already in
              the buyer's outstanding balance, so adding the total again would
              double-count it — and UpdateSale carries no credit guard, so a
              "blocked" banner there would claim a control that doesn't exist.
              `amount` is the credit TAKEN, matching what the server guards on:
              asking about the gross total would block a sale the server allows,
              and the owner's approval tick would then silently do nothing. */}
          {!editingId && (
            <BuyerCreditNotice
              buyerId={buyer ? parseInt(buyer) : null}
              amount={showPayments ? pay.outstanding : invoiceTotal}
              override={creditOverride}
              onOverrideChange={setCreditOverride}
            />
          )}
          <div>
            <Label htmlFor="bsNotes">Notes</Label>
            <textarea id="bsNotes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" rows={2} className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" size="sm" disabled={saving || !birdCount || !totalAmount || paymentsExceedTotal}>{saving ? "Saving..." : editingId ? "Update Sale" : "Record Sale"}</Button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={deleteConfirmId !== null}
        title="Delete Sale"
        message={
          <>
            {deleteSaleMessage("bird", entries.find((e) => e.id === deleteConfirmId))}
            {paidWarning && (
              <div className="mt-4 rounded-lg bg-warning-50 p-3 text-sm text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
                {paidWarning}
              </div>
            )}
          </>
        }
        confirmLabel={paidWarning ? "Delete anyway" : "Delete"}
        busy={deleting}
        busyLabel="Deleting…"
        onConfirm={handleDelete}
        onClose={closeDelete}
      />
    </>
  );
}
